import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'environments/environment';
import { DateTime } from 'luxon';
import { BehaviorSubject, Observable, catchError, combineLatest, map, of, switchMap, take, tap, throwError } from 'rxjs';

import { AuthUtils } from 'app/core/auth/auth.utils';
import { UserService } from 'app/core/user/user.service';
import { Common } from 'app/modules/_common/service';
import { WeekDayAllocationType, WeekType } from 'app/modules/teams/calendar/calendar.type';

@Injectable({providedIn: 'root'})
export class TeamCalendarService
{
    get BASEURL(): string
    {
        return environment.api.replace("[hostinglocation]", AuthUtils.hostingLocation) + '/api/v2/';
    }

    constructor(private _httpClient: HttpClient)
    {
        if (!AuthUtils.hostingLocation) {
            window.location.href = '/sign-out';
        }
    }

    //
    private _my_weeks: BehaviorSubject<WeekType[] | null> = new BehaviorSubject(null);
    private _my_week_day_allocation: BehaviorSubject<WeekDayAllocationType | null> = new BehaviorSubject(null);

    get getMyWeeks$(): Observable<WeekType[]> {
        return this._my_weeks.asObservable();
    }

    // updateMyWeeks(weeks: WeekType[]): void {
    //     this._my_weeks.next(weeks);
    // }
    get getMyWeekDayAllocation$(): Observable<WeekDayAllocationType> {
        return this._my_week_day_allocation.asObservable();
    }

    private _getFilterValues(): { excludeDaysWithNoBookings: boolean; isOfficeDays: boolean } {
        const filters = UserService.preference_calendar_filter;
        return {
            excludeDaysWithNoBookings: filters.find(f => f.name === 'excludeDaysWithNoBookings')?.isSelected ?? false,
            isOfficeDays: filters.find(f => f.name === 'isOfficeDays')?.isSelected ?? false
        };
    }

    getMyWeeks({ personId }, includeWeekends, excludeDaysWithNoBookings: boolean, isOfficeDays: boolean, additionalFilter?: 'all' | 'bookings' | 'no-bookings' | 'office-days'): Observable<WeekType[]> {
        var page = 1;
        var pageSize = 100;

        return this._httpClient.get<any>(`${this.BASEURL}person/week?timezone=${DateTime.now().zoneName}&includeWeekends=${includeWeekends}&isExcludeDaysWithNoBookings=${excludeDaysWithNoBookings}&isOfficeDaysOnly=${isOfficeDays}&page=${page}&pageSize=${pageSize}`).pipe(
            catchError(Common.CatchError),
            map(({ currentWeek }) => {
                var today = DateTime.now().toISODate();

                var id = 0;
                const list = currentWeek.days.map((r) => {

                    id++;

                    return {
                        id: id,
                        type: r.title ?? 'Permanent Allocation',
                        isToday: today == r.date,

                        sequence: r.sequence,
                        title: r.title,
                        message: r.message,
                        dayOfWeek: r.dayOfWeek,
                        dayOfWeekCode: r.dayOfWeekCode,
                        date: r.date,
                        allocations: r.allocations,
                        workDayTypeId: r.workDayTypeId,
                        workDayType: r.workDayType,

                        assetTypeIcon: r.allocations.lenght > 0 ? r.allocations[0].assetTypeIcon : ''
                    } as WeekType;
                });

                if (additionalFilter) {
                    let filteredDays = list;
                    if (additionalFilter === 'bookings') {
                        filteredDays = filteredDays.filter((day: any) =>
                            day.allocations && day.allocations.length > 0
                        );
                    } else if (additionalFilter === 'no-bookings') {
                        filteredDays = filteredDays.filter((day: any) =>
                            !day.allocations || day.allocations.length === 0
                        );
                    }

                    this._my_weeks.next(filteredDays);

                    return filteredDays;
                }

                this._my_weeks.next(list);

                return list;
            })
        );
    }
    getMyWeekDayAllocation(personAssetId, assetId): Observable<WeekDayAllocationType> {
        return combineLatest([
            this._my_weeks.pipe(
                take(1),
                map((records) => {
                    const find = (array, personAssetId) => {
                        var result;

                        array.some(o => {
                            result = (o.personAssetId === personAssetId ? o : find(o.allocations || [], personAssetId));
                            if (result) {
                                result.bookingPeriods = result.bookingPeriods ?? [];
                                //result.parent = o;
                            }
                            return result;
                        });

                        return result;
                    };

                    const rec = find(records, personAssetId);

                    /*this._my_week_day_allocation.next(rec);*/

                    return rec;
                }),
                switchMap((rec) => {
                    if (!rec) {
                        return throwError('Could not find record with id of ' + personAssetId);
                    }

                    return of(rec);

                    //return of({}).pipe(switchMap(r => this.getAssetById(rec.assetId).pipe(map(r => { return rec; }))));
                })
            ),

            //because some details are missing from calendar
            this.getAssetById(assetId)
        ])
            .pipe(map((zz: any) => {
                const rec = {
                    personAssetId: zz[0].personAssetId,
                    title: zz[0].title,
                    details: zz[0].details,

                    siteLevelId: zz[0].siteLevelId,
                    siteLevelName: zz[0].siteLevelName,

                    assetTypeId: zz[0].assetTypeId,
                    assetId: zz[0].assetId,
                    assetName: zz[0].assetName,
                    assetType: zz[0].assetType,

                    canCheckOut: zz[0].canCheckOut,
                    canCheckIn: zz[0].canCheckIn,
                    canCancelReservation: zz[0].canCancelReservation,

                    expiryLocalDate: zz[0].expiryLocalDate,
                    expiryLocalTime: zz[0].expiryLocalTime,
                    expiryClientDate: zz[0].expiryClientDate,

                    startLocalDate: zz[0].startLocalDate,
                    startLocalTime: zz[0].startLocalTime,
                    startClientDate: zz[0].startClientDate,

                    sequence: zz[0].sequence,
                    dayOfWeek: zz[0].dayOfWeek,
                    isPermanent: zz[0].isPermanent,
                    isReserved: zz[0].isReserved,
                    isTemporary: zz[0].isTemporary,
                    isToday: zz[0].isToday,

                    _neighbourhoodColour: zz[1].neighbourhoodColour,
                    _neighbourhoodName: zz[1].neighbourhoodName,
                    _siteLocationId: zz[1].siteLocationId,
                    _siteLocationName: zz[1].siteLocationName,
                    _cityName: zz[1].cityName,
                    _isFavourite: zz[1].isFavourite

                } as WeekDayAllocationType;

                //console.warn(zz[0]);
                //console.warn(zz[1]);

                this._my_week_day_allocation.next(rec);

                return rec;
            }));

        //return of([]).pipe(tap(() => {
        //    this._my_week.next(week_data.find(r => r.id == id));
        //}));
    }
    //

    reserve({ personId, assetId, reservationDateLocal, reservationUntilLocal }, includeWeekends): Observable<any> {
        const data = {
            assetId: assetId,
            personId: personId,
            reservationDateLocal: reservationDateLocal,
            reservationUntilLocal: reservationUntilLocal,
            isAllowTodayReservations: true
        };
        const filterValues = this._getFilterValues();
        return this._httpClient.post<any>(`${this.BASEURL}asset/reservation`, data).pipe(
            catchError(Common.CatchError),
            tap((result) => { }),
            switchMap(records => this.getMyWeeks({ personId: personId }, includeWeekends, filterValues.excludeDaysWithNoBookings, filterValues.isOfficeDays).pipe(map(() => { return true; })))
        );
    }
    checkIn({ allocationAddedFromId, assetId, bookingDurationHours, personId }, includeWeekends): Observable<any> {
        const data = {
            allocationAddedFromId: allocationAddedFromId,
            assetId: assetId,
            bookingDurationHours: bookingDurationHours,
            personId: personId
            //isQuestionnaireCompleted: isQuestionnaireCompleted,
        };
        const filterValues = this._getFilterValues();
        return this._httpClient.post<any>(`${this.BASEURL}asset/addBooking`, data).pipe(
            catchError(Common.CatchError),
            tap((result) => { }),
            switchMap(records => this.getMyWeeks({ personId: personId }, includeWeekends, filterValues.excludeDaysWithNoBookings, filterValues.isOfficeDays).pipe(map(() => { return true; }))),
        );
    }
    checkOut({ personId, assetId }, includeWeekends): Observable<any> {
        const filterValues = this._getFilterValues();
        return this._httpClient.delete<any>(`${this.BASEURL}asset/CheckoutBookingPersonAsset/${personId}/${assetId}`).pipe(
            catchError(Common.CatchError),
            tap((result) => { }),
            switchMap(records => this.getMyWeeks({ personId: personId }, includeWeekends, filterValues.excludeDaysWithNoBookings, filterValues.isOfficeDays).pipe(map(() => { return true; }))),
        );
    }
    cancelReservation({ personId: personId, personAssetId}, includeWeekends): Observable<any> {
        const filterValues = this._getFilterValues();
        return this._httpClient.delete<any>(`${this.BASEURL}asset/reservation/${personAssetId}`).pipe(
            catchError(Common.CatchError),
            tap((result) => { }),
            switchMap(records => this.getMyWeeks({ personId: personId }, includeWeekends, filterValues.excludeDaysWithNoBookings, filterValues.isOfficeDays).pipe(map(() => { return true; }))),
        );
    }

    private _assetType: BehaviorSubject<any | null> = new BehaviorSubject(null);
    get getAssetTypeById$(): Observable<WeekType> {
        return this._assetType.asObservable();
    }

    getAssetTypeById(assetTypeId) {
        return this._httpClient.get<any>(`${this.BASEURL}assettype/${assetTypeId}`).pipe(
            map((data) => {
                try {
                    this._assetType.next(data);

                    return data;

                } catch (e) {

                    console.error(e);

                    return null;
                }
            })
        );
    }
    //

    //
    private _asset: BehaviorSubject<any | null> = new BehaviorSubject(null);
    get getAssetById$(): Observable<any> {
        return this._asset.asObservable();
    }

    getAssetById(assetId) {
        return this._httpClient.get<any>(`${this.BASEURL}teams/asset/${assetId}`).pipe(
            map((data) => {
                try {
                    this._asset.next(data);

                    return data;

                } catch (e) {

                    console.error(e);

                    return null;
                }
            })
        );
    }
    //

    private _attributes: BehaviorSubject<any | null> = new BehaviorSubject(null);
    get getAttributesByAssetType$(): Observable<WeekType> {
        return this._attributes.asObservable();
    }

    getAttributesByAssetType(assetId) {
        return this._httpClient.get<any>(`${this.BASEURL}attribute/GetAttributesByItemId/asset/${assetId}`).pipe(
            map((data) => {
                try {
                    this._attributes.next(data);

                    return data;

                } catch (e) {

                    console.error(e);

                    return null;
                }
            })
        );
    }

    //

    private _personStatus: BehaviorSubject<any | null> = new BehaviorSubject(null);
    get getPersonStatus$(): Observable<any> {
        return this._personStatus.asObservable();
    }

    getPersonStatus() {
        return this._httpClient.get<any>(`${this.BASEURL}personstatus`).pipe(
            map((data) => {
                try {
                    this._personStatus.next(data);

                    return data;

                } catch (e) {

                    console.error(e);

                    return [];
                }
            })
        );
    }

    updatePersonStatus(personId, data): Observable<any> {
        return this._httpClient.put<any>(`${this.BASEURL}person/${personId}/setstatus`, data).pipe(
            catchError(Common.CatchError),
            map((result) => {
                return result;
            }),
        );
    }
    //
}
